import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { isEmail } from "validator";
import isValid from "date-fns/isValid";
import format from "date-fns/format";
// UserService for page-local services lives under src/pages/services
import UserDataService from "./services/UserService";
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Typography,
  Box,
  InputAdornment,
  Chip,
  Container,
  Paper,
  Alert,
  Breadcrumbs,
  Stack,
  IconButton,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlined from '@mui/icons-material/LockOutlined';
import AutoAwesome from '@mui/icons-material/AutoAwesome';

// Clean string by removing titles, punctuation, and extra whitespace for fuzzy match
const cleanNameTokens = (str) => {
  if (!str || typeof str !== 'string') return [];
  const clean = str
    .toLowerCase()
    .replace(/\b(dr|doctor|mr|mrs|miss|ms|alhaji|alhaja|engr|engineer|chief|imam|pastor|barrister|barr)\b\.?/gi, ' ')
    .replace(/[^a-z0-9\s]/gi, ' ')
    .trim();
  return clean.split(/\s+/).filter((w) => w.length > 2);
};

// Find closest matching member for a typed referee string
const findSuggestedRefereeMatch = (typedName, options) => {
  if (!typedName || typeof typedName !== 'string' || typedName.trim().length < 3) return null;
  const typedTokens = cleanNameTokens(typedName);
  if (typedTokens.length === 0) return null;

  let bestMatch = null;
  let highestScore = 0;

  for (const opt of options) {
    const memberTokens = cleanNameTokens(opt.fullName || opt.label);
    if (memberTokens.length === 0) continue;

    let matchedTokens = 0;
    for (const t of typedTokens) {
      if (memberTokens.some((m) => m.includes(t) || t.includes(m))) {
        matchedTokens++;
      }
    }

    const score = matchedTokens / Math.max(typedTokens.length, memberTokens.length);
    if (score >= 0.5 && score > highestScore) {
      highestScore = score;
      bestMatch = opt;
    }
  }

  return highestScore >= 0.5 ? bestMatch : null;
};

// full list of Nigerian states for the state selector
const NIGERIAN_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","Gombe","Imo","Jigawa",
  "Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa","Niger",
  "Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba","Yobe","Zamfara","FCT"
];

const normalizeMembershipStatus = (st) => {
  if (!st) return "Active";
  const s = String(st).trim().toLowerCase();
  if (s === "withdraw" || s === "withdrawn" || s === "withdrawal") return "Withdraw";
  if (s === "expel" || s === "expelled") return "Expel";
  if (s === "decease" || s === "deceased") return "Decease";
  if (s === "non-active" || s === "inactive" || s === "non active") return "Non-Active";
  return "Active";
};

const EditUser = () => {
  // detect current user for role-based view-only
  const { user: currentUser } = useSelector((state) => state.auth || {});
  const storedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try {
      const s = localStorage.getItem("user") || localStorage.getItem("profile");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }, [currentUser]);
  const viewAsRole = sessionStorage.getItem("mtc_view_as_role");

  const isPresident = useMemo(() => {
    if (viewAsRole) return viewAsRole.toLowerCase().includes("president") || viewAsRole.toLowerCase().includes("financial");
    const roles = storedUser?.roles ?? storedUser?.role ?? storedUser?.rolesList ?? null;
    if (!roles) return false;
    if (typeof roles === "string") return roles.toLowerCase().includes("president") || roles.toLowerCase().includes("financial");
    if (Array.isArray(roles)) return roles.some(r => {
      const rs = String(r).toLowerCase();
      return rs.includes("president") || rs.includes("financial");
    });
    return false;
  }, [storedUser, viewAsRole]);

  const isAdmin = useMemo(() => {
    if (viewAsRole) return viewAsRole.toLowerCase().includes("admin");
    const roles = storedUser?.roles ?? storedUser?.role ?? storedUser?.rolesList ?? null;
    if (!roles) return false;
    if (typeof roles === "string") return roles.toLowerCase().includes("admin");
    if (Array.isArray(roles)) return roles.some(r => String(r).toLowerCase().includes("admin"));
    return false;
  }, [storedUser, viewAsRole]);

  const isRegistrar = useMemo(() => {
    if (viewAsRole) return viewAsRole.toLowerCase().includes("registrar") || viewAsRole.toLowerCase().includes("secretary");
    const roles = storedUser?.roles ?? storedUser?.role ?? storedUser?.rolesList ?? null;
    if (!roles) return false;
    if (typeof roles === "string") return roles.toLowerCase().includes("registrar") || roles.toLowerCase().includes("secretary");
    if (Array.isArray(roles)) return roles.some(r => String(r).toLowerCase().includes("registrar") || String(r).toLowerCase().includes("secretary"));
    return false;
  }, [storedUser, viewAsRole]);

  // helper: normalize any date/ISO string to YYYY-MM-DD for <input type="date">
  const formatToInputDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return isValid(d) ? format(d, "yyyy-MM-dd") : "";
  };

  // maximum allowed DOB (must be at least 18 years old)
  const maxDobDate = new Date();
  maxDobDate.setFullYear(maxDobDate.getFullYear() - 18);
  const maxDobInput = formatToInputDate(maxDobDate);
  
  const { id } = useParams();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState(""); // DOB
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sex, setSex] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [stateName, setStateName] = useState("");
  const [homeTown, setHomeTown] = useState("");
  const [residenceAddress, setResidenceAddress] = useState("");
  const [postalAddress, setPostalAddress] = useState("");
  const [applicantsPlaceOfWork, setApplicantsPlaceOfWork] = useState("");
  const [applicantDate, setApplicantDate] = useState("");
  const [nameOfReferee, setNameOfReferee] = useState("");
  const [refereeAddress, setRefereeAddress] = useState("");
  const [refereePhoneNumber, setRefereePhoneNumber] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [refereeDate, setRefereeDate] = useState("");
  const [approvedOrNotApproved, setApprovedOrNotApproved] = useState("Active");
  const [statusDate, setStatusDate] = useState("");
  const [registrationNo, setRegistrationNo] = useState("");
  const [membershipId, setMembershipId] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [targetPosition, setTargetPosition] = useState("");
  const [password, setPassword] = useState(""); // update password (optional)
  const [message, setMessage] = useState("");
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [fetchedId, setFetchedId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const originalRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  // Referee members list for autocomplete lookup
  const [refereeMembers, setRefereeMembers] = useState([]);
  const [loadingReferees, setLoadingReferees] = useState(false);

  useEffect(() => {
    setLoadingReferees(true);
    UserDataService.getRefereeMembers(id)
      .then((res) => {
        const list = res?.data || res || [];
        setRefereeMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoadingReferees(false));
  }, [id]);

  const refereeOptions = useMemo(() => {
    const currentId = String(fetchedId || id || "");
    return refereeMembers
      .filter((m) => {
        const mid = String(m._id || m.id || "");
        return mid && mid !== currentId;
      })
      .map((m) => {
        const mid = String(m._id || m.id);
        const fname = (m.firstName || m.firstname || m.first_name || "").trim();
        const lname = (m.lastName || m.lastname || m.last_name || "").trim();
        const fullName = (lname ? `${lname} ${fname}`.trim() : fname) || (m.username || m.name || "").trim() || "Member";
        const regNo = (m.registrationNo || m.membershipId || "").trim();
        const label = regNo ? `${fullName} (${regNo})` : fullName;
        return {
          id: mid,
          label,
          fullName,
          regNo,
          raw: m,
        };
      })
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [refereeMembers, id, fetchedId]);

  // Smart Form Assistant: Find match if referee name is present but phone or email is missing
  const suggestedRefereeMatch = useMemo(() => {
    if (!nameOfReferee || nameOfReferee.trim().length < 3) return null;
    return findSuggestedRefereeMatch(nameOfReferee, refereeOptions);
  }, [nameOfReferee, refereeOptions]);

  const handleApplySuggestedReferee = (match) => {
    if (!match) return;
    setNameOfReferee(match.fullName || match.label || "");
    if (match.raw?.phoneNumber) setRefereePhoneNumber(match.raw.phoneNumber);
    if (match.raw?.email) setRefereeEmail(match.raw.email);
    if (match.raw?.residenceAddress || match.raw?.postalAddress) {
      setRefereeAddress(match.raw.residenceAddress || match.raw.postalAddress || "");
    }
  };

  useEffect(() => {
    if (!id) return;
    UserDataService.get(id)
      .then((response) => {
        const u = response.data || {};
        // remember actual backend document id (use for update/delete)
        setFetchedId(u._id || u.id || null);
        setUsername(u.username || "");
        setEmail(u.email || "");
        setDate(formatToInputDate(u.date || u.dob || ""));
        setFirstName(u.firstName || u.first_name || "");
        setMiddleName(u.middleName || u.middle_name || "");
        setLastName(u.lastName || u.last_name || "");
        setPhoneNumber(u.phoneNumber || u.phone || "");
        setSex(u.sex || u.gender || "");
        setMaritalStatus(u.maritalStatus || u.marital_status || "");
        setStateName(u.state || "");
        setHomeTown(u.homeTown || u.home_town || "");
        setResidenceAddress(u.residenceAddress || u.residence_address || "");
        setPostalAddress(u.postalAddress || u.postal_address || "");
        setApplicantsPlaceOfWork(u.applicantsPlaceOfWork || u.placeOfWork || "");
        setApplicantDate(formatToInputDate(u.applicantDate || ""));
        setNameOfReferee(u.nameOfReferee || "");
        setRefereeAddress(u.refereeAddress || "");
        setRefereePhoneNumber(u.refereePhoneNumber || "");
        setRefereeEmail(u.refereeEmail || "");
        setRefereeDate(formatToInputDate(u.refereeDate || ""));
        setApprovedOrNotApproved(normalizeMembershipStatus(u.approvedOrNotApproved || u.approvalStatus));
        setStatusDate(formatToInputDate(u.statusDate || u.dateApproved || ""));
        setRegistrationNo(u.registrationNo || u.registration_number || u.regNo || "");
        setMembershipId(u.membershipId || u.membership_id || "");
        setTargetPosition(u.position || "");
        
        const rawRoles = u.roles || [];
        const mappedRoles = rawRoles.map(r => r.name || r).filter(r => typeof r === "string");
        setSelectedRoles(mappedRoles);

        // store original snapshot for dirty-check
        originalRef.current = {
          username: u.username || "",
          email: u.email || "",
          date: formatToInputDate(u.date || u.dob || ""),
          firstName: u.firstName || u.first_name || "",
          middleName: u.middleName || u.middle_name || "",
          lastName: u.lastName || u.last_name || "",
          phoneNumber: u.phoneNumber || u.phone || "",
          sex: u.sex || u.gender || "",
          maritalStatus: u.maritalStatus || u.marital_status || "",
          stateName: u.state || "",
          homeTown: u.homeTown || u.home_town || "",
          residenceAddress: u.residenceAddress || u.residence_address || "",
          postalAddress: u.postalAddress || u.postal_address || "",
          applicantsPlaceOfWork: u.applicantsPlaceOfWork || u.placeOfWork || "",
          applicantDate: formatToInputDate(u.applicantDate || ""),
          nameOfReferee: u.nameOfReferee || "",
          refereeAddress: u.refereeAddress || "",
          refereePhoneNumber: u.refereePhoneNumber || "",
          refereeEmail: u.refereeEmail || "",
          refereeDate: formatToInputDate(u.refereeDate || ""),
          approvedOrNotApproved: normalizeMembershipStatus(u.approvedOrNotApproved || u.approvalStatus),
          statusDate: formatToInputDate(u.statusDate || u.dateApproved || ""),
          registrationNo: u.registrationNo || u.registration_number || u.regNo || "",
          rolesStr: mappedRoles.join(",")
        };
        setIsDirty(false);
      })
      .catch((e) => {
        console.error(e);
        setMessage("Failed to load user.");
      });
  }, [id]);

  // compute dirty flag when any field/value changes
  useEffect(() => {
    const orig = originalRef.current;
    if (!orig) return;
    const changed =
      username !== orig.username ||
      email !== orig.email ||
      date !== orig.date ||
      firstName !== orig.firstName ||
      middleName !== orig.middleName ||
      lastName !== orig.lastName ||
      phoneNumber !== orig.phoneNumber ||
      sex !== orig.sex ||
      maritalStatus !== orig.maritalStatus ||
      stateName !== orig.stateName ||
      homeTown !== orig.homeTown ||
      residenceAddress !== orig.residenceAddress ||
      postalAddress !== orig.postalAddress ||
      applicantsPlaceOfWork !== orig.applicantsPlaceOfWork ||
      applicantDate !== orig.applicantDate ||
      nameOfReferee !== orig.nameOfReferee ||
      refereeAddress !== orig.refereeAddress ||
      refereePhoneNumber !== orig.refereePhoneNumber ||
      refereeEmail !== orig.refereeEmail ||
      refereeDate !== orig.refereeDate ||
      approvedOrNotApproved !== orig.approvedOrNotApproved ||
      statusDate !== orig.statusDate ||
      registrationNo !== orig.registrationNo ||
      selectedRoles.join(",") !== orig.rolesStr ||
      // password counts as change when non-empty
      (password && password.length > 0);
    setIsDirty(Boolean(changed));
  }, [
    username, email, date, firstName, middleName, lastName, phoneNumber, sex,
    maritalStatus, stateName, homeTown, residenceAddress, postalAddress,
    applicantsPlaceOfWork, applicantDate, nameOfReferee, refereeAddress,
    refereePhoneNumber, refereeEmail, refereeDate, approvedOrNotApproved,
    statusDate, registrationNo, password, selectedRoles
  ]);

  const validatePhone = (phone) => {
    if (!phone) return false;
    const cleaned = phone.toString().trim().replace(/[\s-.()]/g, '');
    if (/^(?:\+234)\d{10}$/.test(cleaned) || /^0\d{10}$/.test(cleaned)) return true; // Nigeria
    if (/^(?:\+44)\d{10}$/.test(cleaned) || /^0\d{10}$/.test(cleaned)) return true; // UK
    if (/^(?:\+1)\d{10}$/.test(cleaned) || /^\d{10}$/.test(cleaned)) return true; // US
    return false;
  };

  const isTargetOfficer = useMemo(() => {
    const officerRoles = [
      "admin",
      "president",
      "registrar",
      "secretary",
      "business",
      "financial",
      "financial_1",
      "financial_2",
      "financial_3"
    ];
    const hasOfficerRole = Array.isArray(selectedRoles) && selectedRoles.some((r) => {
      const lower = String(r).toLowerCase().trim();
      return officerRoles.includes(lower) || lower.startsWith("financial") || (lower !== "user" && lower !== "");
    });
    const pos = String(targetPosition || "").toLowerCase().trim();
    const hasOfficerPos =
      pos.includes("admin") ||
      pos.includes("president") ||
      pos.includes("registrar") ||
      pos.includes("secretary") ||
      pos.includes("business") ||
      pos.includes("financial");

    return Boolean(hasOfficerRole || hasOfficerPos);
  }, [selectedRoles, targetPosition]);

  // Locking rules:
  // - President: view only (all fields locked)
  // - Registrar / Non-Admin: locked for all executive officers (Admin, President, Registrar, Business, Financial I, II, III).
  //   Only general members with user role can have their password updated by a Registrar.
  // - Administrator: full authority to update passwords for all accounts.
  const isPasswordLocked = isPresident || (!isAdmin && isTargetOfficer);
  const canEditPassword = !isPasswordLocked;

  const handleUpdate = async (e) => {
     e.preventDefault();
     if (isPresident) { setMessage("View only — you cannot update this user."); return; }
     if (password && isPasswordLocked) {
       setMessage("Permission denied: Registrars cannot update passwords for executive officers (Admin, President, Registrar, Business, Financial I, II, III). Only general members with the user role can have their password updated.");
       return;
     }
     if (password && password.trim().length > 0 && password.trim().length < 6) {
       setMessage("New password must be at least 6 characters long.");
       return;
     }

     setMessage("");
     setLoadingUpdate(true);

     // validations
     if (!username || username.length < 3) { setMessage("Invalid username"); setLoadingUpdate(false); return; }
     if (!email || !isEmail(email)) { setMessage("Invalid email"); setLoadingUpdate(false); return; }
     if (!firstName || !lastName) { setMessage("First and last name required"); setLoadingUpdate(false); return; }
     if (registrationNo && !/^\d{6}$/.test(String(registrationNo).trim())) { setMessage("Registration number must be exactly 6 digits"); setLoadingUpdate(false); return; }
     if (!phoneNumber || !validatePhone(phoneNumber)) { setMessage("Invalid phone number"); setLoadingUpdate(false); return; }
     if (!date || !isValid(new Date(date))) { setMessage("Invalid date"); setLoadingUpdate(false); return; }
     if (new Date(date) > maxDobDate) { setMessage("Member must be at least 18 years old"); setLoadingUpdate(false); return; }

     const toISO = (v) => (v ? new Date(v).toISOString() : null);
     const payload = {
       username,
       email,
       date: toISO(date),
       firstName,
       middleName,
       lastName,
       phoneNumber,
       sex,
       maritalStatus,
       state: stateName,
       homeTown,
       applicantsPlaceOfWork,
       applicantDate: toISO(applicantDate),
       nameOfReferee,
       refereeAddress,
       refereePhoneNumber,
       refereeEmail,
       refereeDate: toISO(refereeDate),
       residenceAddress,
       postalAddress,
       approvedOrNotApproved,
       statusDate: toISO(statusDate),
       dateApproved: toISO(statusDate),
       registrationNo,
     };
     
     if (isAdmin && selectedRoles.length > 0) {
       payload.roles = selectedRoles;
     }
     // include common password keys to maximize backend compatibility (only if authorized)
     if (password && canEditPassword) {
       payload.password = password;
       payload.newPassword = password;
     }
 
     try {
       const targetId = fetchedId || id;
       const res = await UserDataService.update(targetId, payload);
       if (res && (res.status === 200 || res.status === 204 || res.data)) {
         setMessage("User updated successfully.");
         navigate("/dashboard/users");
       } else {
         throw new Error("Update failed.");
       }
     } catch (error) {
       console.error("Update user error:", error);
       const errMsg = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Update failed. Check network/backend.";
       setMessage(errMsg);
     } finally {
       setLoadingUpdate(false);
     }
   };
 
   // delete user with confirmation
   const handleDelete = async () => {
     if (isPresident || isRegistrar) { setMessage("View only — you cannot delete this user."); return; }
     if (!id) return;
     const ok = window.confirm("Delete this member? This action cannot be undone.");
     if (!ok) return;
     setMessage("");
    setLoadingDelete(true);
    try {
      const delFn = UserDataService && (UserDataService.remove || UserDataService.delete || UserDataService.deleteUser || UserDataService.deleteMember);
      if (!delFn || typeof delFn !== "function") throw new Error("Delete method not available on user service");
      await delFn(id);
      setMessage("User deleted.");
      navigate("/dashboard/users");
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.message || "Delete failed.");
    } finally {
      setLoadingDelete(false);
    }
   };

  const toggleShowPassword = () => setShowPassword((s) => !s);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
      {/* Breadcrumb Navigation & Top Action */}
      <Box sx={{ mb: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link to="/dashboard" style={{ textDecoration: "none", color: "#64748B", fontWeight: 500, fontSize: "0.875rem" }}>
            Dashboard
          </Link>
          <Link to="/dashboard/users" style={{ textDecoration: "none", color: "#64748B", fontWeight: 500, fontSize: "0.875rem" }}>
            Members
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
            {isPresident ? "View Member" : "Edit Member"}
          </Typography>
        </Breadcrumbs>

        <Button
          component={Link}
          to="/dashboard/users"
          variant="outlined"
          size="small"
          startIcon={<ArrowBack />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            borderColor: "#CBD5E1",
            color: "#334155",
            "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
          }}
        >
          Back to Members
        </Button>
      </Box>

      {/* Main Container Paper */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 20px -2px rgba(11, 59, 96, 0.05)",
          overflow: "hidden",
        }}
      >
        {/* Navy Header Banner */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0B3B60 0%, #072740 100%)",
            color: "#FFFFFF",
            p: { xs: 2.5, sm: 3.5 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {isPresident ? "Member Profile Details" : "Edit Member Account"}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)", mt: 0.5 }}>
                {firstName || lastName ? `${firstName} ${lastName}`.trim() : username || "Cooperative Member"}
                {email ? ` • ${email}` : ""}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {registrationNo && (
                <Chip
                  size="small"
                  label={`Reg No: ${registrationNo}`}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.15)",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                />
              )}
              {membershipId && (
                <Chip
                  size="small"
                  label={`Member ID: ${membershipId}`}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.15)",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                />
              )}
              {approvedOrNotApproved && (
                <Chip
                  size="small"
                  label={approvedOrNotApproved}
                  sx={{
                    fontWeight: 700,
                    backgroundColor:
                      approvedOrNotApproved === "Active" ? "#059669" :
                      approvedOrNotApproved === "Non-Active" ? "#D97706" :
                      approvedOrNotApproved === "Withdraw" ? "#854D0E" :
                      approvedOrNotApproved === "Expel" ? "#DC2626" :
                      approvedOrNotApproved === "Decease" ? "#475569" : "#059669",
                    color: "#FFFFFF",
                  }}
                />
              )}
            </Stack>
          </Box>
        </Box>

        {/* Content Body */}
        <Box sx={{ p: { xs: 2.5, sm: 3.5 }, backgroundColor: "#F8FAFC" }}>
          {message && (
            <Alert
              severity={message.toLowerCase().includes("fail") || message.toLowerCase().includes("error") ? "error" : "success"}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {message}
            </Alert>
          )}

          {isPresident ? (
            // Full Read-Only Structured View for President
            <Stack spacing={3}>
              {/* Identity & Credentials */}
              <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                  Membership & Account Credentials
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Registration Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{registrationNo || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Membership ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{membershipId || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Username</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{username || "—"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Personal Information */}
              <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                  Personal Information
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>First Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{firstName || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Middle Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{middleName || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Last Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{lastName || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Gender</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{sex || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Marital Status</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{maritalStatus || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Date of Birth</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{date || "—"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Contact & Location */}
              <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                  Contact & Residence
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Email Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{email || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Phone Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{phoneNumber || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>State of Origin</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{stateName || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Home Town</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{homeTown || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Place of Work</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{applicantsPlaceOfWork || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Residence Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{residenceAddress || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Postal Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{postalAddress || "—"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Referee & Cooperative Status */}
              <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                  Referee & Cooperative Status
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{nameOfReferee || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Phone</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{refereePhoneNumber || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Email</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{refereeEmail || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{refereeAddress || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Applicant Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{applicantDate || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{refereeDate || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Approval Status</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{approvedOrNotApproved || "Active"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Status Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{statusDate || "—"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
                <Button component={Link} to="/dashboard/users" variant="outlined" sx={{ textTransform: "none", fontWeight: 600 }}>
                  Back to Members
                </Button>
              </Box>
            </Stack>
          ) : (
            // Full Editable Modern View for Admin & Officers
            <form onSubmit={handleUpdate} noValidate>
              <Stack spacing={3}>
                {/* 1. Membership & Credentials */}
                <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                    Membership & Account Credentials
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Registration Number"
                        variant="outlined"
                        value={registrationNo}
                        onChange={(e) => setRegistrationNo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="e.g. 000101"
                        inputProps={{ inputMode: "numeric", maxLength: 6 }}
                        helperText="Must be exactly 6 digits (must be unique)"
                        required
                        disabled={isPresident}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Membership ID"
                        variant="outlined"
                        value={membershipId || "—"}
                        disabled
                        helperText="Auto-generated incremental ID (immutable)"
                      />
                    </Grid>

                    {isAdmin && !isPresident && (
                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined" disabled={isPresident}>
                          <InputLabel>Assigned Roles & Permissions</InputLabel>
                          <Select
                            multiple
                            value={selectedRoles}
                            label="Assigned Roles & Permissions"
                            onChange={(e) => {
                              const {
                                target: { value },
                              } = e;
                              setSelectedRoles(typeof value === "string" ? value.split(",") : value);
                            }}
                            renderValue={(selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((val) => {
                                  const labels = {
                                    user: "User",
                                    admin: "Admin",
                                    president: "President",
                                    registrar: "Registrar",
                                    financial: "Financial I",
                                    financial_1: "Financial I",
                                    financial_2: "Financial II",
                                    financial_3: "Financial III",
                                    business: "Business",
                                  };
                                  return <Chip key={val} label={labels[val] || val} size="small" sx={{ fontWeight: 600 }} />;
                                })}
                              </Box>
                            )}
                          >
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="president">President</MenuItem>
                            <MenuItem value="registrar">Registrar</MenuItem>
                            <MenuItem value="financial">Financial I</MenuItem>
                            <MenuItem value="financial_2">Financial II</MenuItem>
                            <MenuItem value="financial_3">Financial III</MenuItem>
                            <MenuItem value="business">Business</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* 2. Personal Information */}
                <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                    Personal Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="First Name" variant="outlined" value={firstName} onChange={(e) => setFirstName(e.target.value)} required disabled={isPresident} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Middle Name" variant="outlined" value={middleName} onChange={(e) => setMiddleName(e.target.value)} disabled={isPresident} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Last Name" variant="outlined" value={lastName} onChange={(e) => setLastName(e.target.value)} required disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined" disabled={isPresident}>
                        <InputLabel>Gender</InputLabel>
                        <Select value={sex} label="Gender" onChange={(e) => setSex(e.target.value)}>
                          <MenuItem value=""><em>None</em></MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined" disabled={isPresident}>
                        <InputLabel>Marital Status</InputLabel>
                        <Select value={maritalStatus} label="Marital Status" onChange={(e) => setMaritalStatus(e.target.value)}>
                          <MenuItem value=""><em>None</em></MenuItem>
                          <MenuItem value="Single">Single</MenuItem>
                          <MenuItem value="Married">Married</MenuItem>
                          <MenuItem value="Divorce">Divorce</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        variant="outlined"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: maxDobInput }}
                        disabled={isPresident}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* 3. Contact & Residence */}
                <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                    Contact & Address Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Username" variant="outlined" value={username} onChange={(e) => setUsername(e.target.value)} required disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Email" type="email" variant="outlined" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Phone Number" variant="outlined" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined" required disabled={isPresident}>
                        <InputLabel>State of Origin</InputLabel>
                        <Select value={stateName} label="State of Origin" onChange={(e) => setStateName(e.target.value)} required>
                          <MenuItem value=""><em>None</em></MenuItem>
                          {NIGERIAN_STATES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Home Town" variant="outlined" value={homeTown} onChange={(e) => setHomeTown(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Place of Work" variant="outlined" value={applicantsPlaceOfWork} onChange={(e) => setApplicantsPlaceOfWork(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Residence Address" variant="outlined" value={residenceAddress} onChange={(e) => setResidenceAddress(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Postal Address" variant="outlined" value={postalAddress} onChange={(e) => setPostalAddress(e.target.value)} disabled={isPresident} />
                    </Grid>
                  </Grid>
                </Box>

                {/* 4. Referee & Application */}
                <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                    Referee & Application Details
                  </Typography>
                  <Grid container spacing={2}>
                    {suggestedRefereeMatch && (!refereePhoneNumber || !refereeEmail) && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: "#F0FDF4",
                            border: "1px solid #86EFAC",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ backgroundColor: "#DCFCE7", p: 1, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <AutoAwesome sx={{ fontSize: 20, color: "#166534" }} />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534", fontSize: "0.85rem", lineHeight: 1.3 }}>
                                Suggested Match: {suggestedRefereeMatch.fullName} {suggestedRefereeMatch.regNo ? `(#${suggestedRefereeMatch.regNo})` : ""}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#15803D", display: "block" }}>
                                Phone: <strong>{suggestedRefereeMatch.raw?.phoneNumber || "N/A"}</strong> • Email: <strong>{suggestedRefereeMatch.raw?.email || "N/A"}</strong>
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleApplySuggestedReferee(suggestedRefereeMatch)}
                            disabled={isPresident}
                            sx={{
                              backgroundColor: "#166534",
                              color: "#ffffff",
                              textTransform: "none",
                              fontSize: "0.78rem",
                              fontWeight: 800,
                              py: 0.7,
                              px: 2,
                              borderRadius: 1.5,
                              boxShadow: "0 2px 6px rgba(22, 101, 52, 0.25)",
                              "&:hover": { backgroundColor: "#14532D" },
                            }}
                          >
                            Apply Auto-Fill
                          </Button>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        freeSolo
                        options={refereeOptions}
                        loading={loadingReferees}
                        value={nameOfReferee || ""}
                        onChange={(event, newValue) => {
                          if (typeof newValue === "object" && newValue !== null) {
                            setNameOfReferee(newValue.fullName || newValue.label || "");
                            if (newValue.raw?.phoneNumber) setRefereePhoneNumber(newValue.raw.phoneNumber);
                            if (newValue.raw?.email) setRefereeEmail(newValue.raw.email);
                            if (newValue.raw?.residenceAddress || newValue.raw?.postalAddress) {
                              setRefereeAddress(newValue.raw.residenceAddress || newValue.raw.postalAddress || "");
                            }
                          } else if (typeof newValue === "string") {
                            setNameOfReferee(newValue);
                          } else if (!newValue) {
                            setNameOfReferee("");
                          }
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === "input") {
                            setNameOfReferee(newInputValue);
                          }
                        }}
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;
                          return option?.label || option?.fullName || "";
                        }}
                        isOptionEqualToValue={(option, val) => {
                          if (typeof val === "string") {
                            return option.fullName === val || option.label === val;
                          }
                          return option.id === val?.id;
                        }}
                        filterOptions={(options, state) => {
                          const query = (state.inputValue || "").toLowerCase().trim();
                          if (!query) return options;
                          return options.filter((opt) => {
                            const label = (opt.label || "").toLowerCase();
                            const reg = (opt.regNo || "").toLowerCase();
                            const full = (opt.fullName || "").toLowerCase();
                            return label.includes(query) || reg.includes(query) || full.includes(query);
                          });
                        }}
                        disabled={isPresident}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            key={option.id}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              py: 1,
                              px: 1.5,
                              borderBottom: "1px solid #F1F5F9",
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                                {option.fullName}
                              </Typography>
                              {option.regNo ? (
                                <Typography variant="caption" sx={{ color: "#0D9488", fontWeight: 600 }}>
                                  ID / Reg: {option.regNo}
                                </Typography>
                              ) : (
                                <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                                  Registered Member
                                </Typography>
                              )}
                            </Box>
                            {option.raw?.email && (
                              <Typography variant="caption" sx={{ color: "#64748B", ml: 1 }}>
                                {option.raw.email}
                              </Typography>
                            )}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label="Referee Name"
                            variant="outlined"
                            placeholder="Type to search existing member..."
                            helperText="Search member to auto-fill phone, email & address"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingReferees ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Referee Phone" variant="outlined" value={refereePhoneNumber} onChange={(e) => setRefereePhoneNumber(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Referee Email" type="email" variant="outlined" value={refereeEmail} onChange={(e) => setRefereeEmail(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Referee Address" variant="outlined" value={refereeAddress} onChange={(e) => setRefereeAddress(e.target.value)} disabled={isPresident} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Applicant Date"
                        type="date"
                        variant="outlined"
                        value={applicantDate}
                        onChange={(e) => setApplicantDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={isPresident}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Referee Date"
                        type="date"
                        variant="outlined"
                        value={refereeDate}
                        onChange={(e) => setRefereeDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={isPresident}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* 5. Status & Security */}
                <Box sx={{ backgroundColor: "#FFFFFF", p: 3, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                    Membership Status & Security
                  </Typography>
                  <Grid container spacing={2}>
                    {isRegistrar && isTargetOfficer && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 1.5, fontSize: "0.8125rem", py: 0.75 }}>
                          <strong>Officer Security Policy:</strong> This member holds an officer designation ({selectedRoles.filter(r => r !== 'user').join(", ") || targetPosition || "Officer"}). Updating passwords for officers (Admin, President, Registrar, Business, Financial I, II, III) is locked for Registrars. Only regular members with the user role can have their password updated by a Registrar.
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined" disabled={isPresident}>
                        <InputLabel>Membership Status</InputLabel>
                        <Select
                          value={approvedOrNotApproved || "Active"}
                          label="Membership Status"
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            setApprovedOrNotApproved(newStatus);
                            const today = new Date().toISOString().slice(0, 10);
                            setStatusDate(today);
                          }}
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Non-Active">Non-Active</MenuItem>
                          <MenuItem value="Withdraw">Withdraw</MenuItem>
                          <MenuItem value="Expel">Expel</MenuItem>
                          <MenuItem value="Decease">Decease</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Status Date"
                        type="date"
                        variant="outlined"
                        value={statusDate}
                        onChange={(e) => setStatusDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={isPresident}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Update Password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={isPasswordLocked ? "Password update locked for this officer" : "Enter new password (optional)"}
                        helperText={
                          isPresident
                            ? "View only"
                            : isPasswordLocked
                            ? "Locked: Registrars cannot update passwords for officers (Admin, President, Registrar, Business, Financial I, II, III). Only members with user role can be updated."
                            : "Leave blank to keep current password (min. 6 characters to update)"
                        }
                        disabled={isPasswordLocked}
                        InputProps={{
                          startAdornment: isPasswordLocked ? (
                            <InputAdornment position="start">
                              <LockOutlined sx={{ color: "#94A3B8", fontSize: 20 }} />
                            </InputAdornment>
                          ) : null,
                          endAdornment: !isPasswordLocked ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={toggleShowPassword}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                              >
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ) : (
                            <InputAdornment position="end">
                              <Chip
                                label="Officer Locked"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.6875rem",
                                  fontWeight: 700,
                                  bgcolor: "#F1F5F9",
                                  color: "#64748B",
                                  border: "1px solid #CBD5E1",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Form Action Buttons */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, pt: 1, flexWrap: "wrap" }}>
                  <Button component={Link} to="/dashboard/users" variant="outlined" sx={{ textTransform: "none", fontWeight: 600 }}>
                    Cancel
                  </Button>

                  {!(isPresident || isRegistrar) && (
                    <Button type="button" variant="outlined" color="error" onClick={handleDelete} disabled={loadingDelete} sx={{ textTransform: "none", fontWeight: 600 }}>
                      {loadingDelete ? "Deleting..." : "Delete User"}
                    </Button>
                  )}

                  {!isPresident && (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isDirty || loadingUpdate}
                      sx={{
                        backgroundColor: "#0B3B60",
                        "&:hover": { backgroundColor: "#072740" },
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                      }}
                    >
                      {loadingUpdate ? "Updating..." : "Update Member"}
                    </Button>
                  )}
                </Box>
              </Stack>
            </form>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default EditUser;